/**
 * The page's side of the FINN Outreach Sender extension (`chrome-extension/outreach-sender`).
 *
 * Chrome lets a listed web page message an extension by id (`externally_connectable`), and that
 * is the whole protocol: `ping` to see whether it is installed, then one `run` per InMail, each
 * awaited before the next. The extension never talks to our proxy — this page records "sent"
 * itself, the same call as "Mark as sent" on the card — and it holds no FINN credential.
 *
 * The id is fixed by the `key` in the manifest, so an unpacked load on every recruiter's machine
 * has the same id; `VITE_OUTREACH_EXTENSION_ID` overrides it for a store-published build.
 */
export const OUTREACH_EXTENSION_ID = import.meta.env.VITE_OUTREACH_EXTENSION_ID || 'lclicahfmibcccdefkknnlcdaglebajn';

export interface SendJob {
  url: string;
  name: string;
  subject: string;
  body: string;
  /** false = type everything, press nothing (the safe test of the selectors). */
  send: boolean;
  /** Put into LinkedIn's own "Follow-up-Nachricht" (sent by LinkedIn after `days` unless they reply). */
  followUp?: { subject: string; body: string; days?: number } | null;
  /** A manual follow-up to someone already contacted: typed only, the tab stays open, the recruiter sends. */
  followUpFill?: boolean;
  /** false = leave the LinkedIn tab open even when it worked (test recipients, Oct 19 2026). */
  closeTab?: boolean;
  /** Test mode, TA managers only: send even though LinkedIn says they were contacted. */
  testRecipient?: boolean;
  /** 0.9.0: part of a run opened with `openSendWindow` — load it in that run's LinkedIn window. */
  inWindow?: boolean;
  /** The usable screen area, for opening that window again beside this one if it was closed. */
  screen?: ReturnType<typeof screenArea>;
}

export interface SendResult {
  ok: boolean;
  step: string;
  error?: string;
  detail?: string;
  sent?: boolean;
  /** true when LinkedIn visibly closed/emptied the composer after Send. */
  verified?: boolean;
  /** LinkedIn's follow-up was added in the composer. */
  followUpScheduled?: boolean;
  /** The days the extension set on LinkedIn's follow-up (0.4.1+). */
  followUpDays?: number | null;
  /** How the body's links went in: as links, as "words (address)", or there were none (0.4.1+). */
  links?: 'links' | 'urls' | 'text';
  /** Test fill only: the composer shows other lines/paragraphs than the InMail (0.7.2+); a real Send stops with `paragraphs_lost`. */
  paragraphsLost?: string | null;
  /** Test fill only: LinkedIn said they were contacted already; it filled anyway and sent nothing. */
  contactedWarning?: string | null;
  /** On a test fill: whether Send could have been pressed. */
  sendButton?: 'enabled' | 'disabled' | 'missing';
}

type ChromeRuntime = {
  runtime?: { sendMessage?: (id: string, msg: unknown, cb: (res: unknown) => void) => void; lastError?: { message?: string } };
};

const runtime = () => (globalThis as { chrome?: ChromeRuntime }).chrome?.runtime;

function message<T>(msg: unknown, timeoutMs: number): Promise<T | null> {
  const rt = runtime();
  const send = rt?.sendMessage;
  if (!rt || !send) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    try {
      send.call(rt, OUTREACH_EXTENSION_ID, msg, (res) => {
        clearTimeout(timer);
        // lastError = no extension with that id, or one that is not listening. Reading it is also
        // what stops Chrome logging "Unchecked runtime.lastError".
        if (rt.lastError || res === undefined) resolve(null);
        else resolve(res as T);
      });
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/** The installed extension's version, or null when it is not there (or not this browser). */
export async function extensionVersion(): Promise<string | null> {
  const r = await message<{ ok: boolean; version: string }>({ type: 'ping' }, 1500);
  return r?.ok ? r.version : null;
}

/** One InMail. Resolves with the extension's own result; a missing answer is reported as such. */
export async function runInExtension(job: SendJob): Promise<SendResult> {
  const r = await message<SendResult>({ type: 'run', job }, 90_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/**
 * Sending in a window beside (0.9.0, Sofia, Nov 2 2026): a Send / Test fill run narrows this window
 * once and opens ONE LinkedIn window beside it; every InMail of the run loads there. Older
 * extensions open a tab per InMail, as before.
 */
export const SEND_WINDOW_MIN_VERSION = '0.9.0';
export const canSendInWindow = (version: string | null | undefined) =>
  Boolean(version) && (version as string).localeCompare(SEND_WINDOW_MIN_VERSION, undefined, { numeric: true }) >= 0;

/** Run start: the LinkedIn window beside this one. */
export async function openSendWindow(): Promise<SendResult> {
  const r = await message<SendResult>({ type: 'send-open', job: { screen: screenArea() } }, 15_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Run end: close it and give this window its size back — or `keep` it open to look at a refusal. */
export async function closeSendWindow(keep = false, { tries = 20, waitMs = 3_000 } = {}): Promise<void> {
  // The extension refuses `send-done` while an InMail is still in flight (`busy`, e.g. its answer was
  // lost in a sleep): asked again for up to ~1 min, so the window and the run do not stay open (CodeRabbit, PR #783).
  for (let n = 0; n < tries; n += 1) {
    const r = await message<SendResult>({ type: 'send-done', job: { keep } }, 10_000);
    if (r?.error !== 'busy') return;
    if (n < tries - 1) await new Promise((resolve) => setTimeout(resolve, waitMs));
  }
}

/** What `read.js` returns from a Recruiter profile tab. */
export interface CaptureResult {
  ok: boolean;
  step: string;
  error?: string;
  detail?: string;
  url?: string;
  name?: string | null;
  headline?: string | null;
  location?: string | null;
  publicUrl?: string | null;
  text?: string;
  /** 0.6.0: collapsed sections opened before reading, and how many were still closed after. */
  expanded?: number;
  markersLeft?: number;
  /** 0.7.1 `capture-me`: characters read per page (profile, experience, education). */
  pages?: Record<string, number>;
}

/** Captures need extension v0.2.0 or newer; an older one answers `unknown_type`. */
export const CAPTURE_MIN_VERSION = '0.2.0';
export const canCapture = (version: string | null | undefined) =>
  Boolean(version) && (version as string).localeCompare(CAPTURE_MIN_VERSION, undefined, { numeric: true }) >= 0;

/** Read one candidate's Recruiter profile in a background tab (opened and closed by the extension). */
export async function captureInExtension(recruiterId: string): Promise<CaptureResult> {
  const url = `https://www.linkedin.com/talent/profile/${encodeURIComponent(recruiterId)}`;
  const r = await message<CaptureResult>({ type: 'capture', job: { url } }, 120_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Read the Recruiter profile the recruiter has open themselves. */
export async function captureOpenTab(): Promise<CaptureResult> {
  const r = await message<CaptureResult>({ type: 'capture-open' }, 60_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Read the profile of whoever is signed in to LinkedIn (linkedin.com/in/me), in a background tab — extension v0.7.0. */
export async function captureOwnProfile(): Promise<CaptureResult> {
  const r = await message<CaptureResult>({ type: 'capture-me' }, 90_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Built from chrome-extension/outreach-sender on every deploy (scripts/build-outreach-extension.mjs). */
export const EXTENSION_ZIP = '/finn-outreach-sender.zip';

/**
 * The version in chrome-extension/outreach-sender/manifest.json, i.e. the one the zip above holds.
 * Bump both together (outreachExtension.test.ts checks it). Older installs see "Please update".
 */
export const LATEST_EXTENSION_VERSION = '0.9.0';
export const isOutdated = (version: string) => version.localeCompare(LATEST_EXTENSION_VERSION, undefined, { numeric: true }) < 0;

/**
 * The composer inside the role's Recruiter project (Oct 27 2026) needs v0.8.0: older versions open
 * only the plain composer and refuse anything else, so they get that one (no "Contacted" in LinkedIn).
 */
export const PROJECT_COMPOSER_MIN_VERSION = '0.8.0';
export const canSendInProject = (version: string | null | undefined) =>
  Boolean(version) && (version as string).localeCompare(PROJECT_COMPOSER_MIN_VERSION, undefined, { numeric: true }) >= 0;

/** "Start sourcing" needs extension v0.3.0 (list.js). */
export const SOURCING_MIN_VERSION = '0.3.0';
export const canSource = (version: string | null | undefined) =>
  Boolean(version) && (version as string).localeCompare(SOURCING_MIN_VERSION, undefined, { numeric: true }) >= 0;

export interface ListResult {
  ok: boolean;
  step: string;
  error?: string;
  detail?: string;
  url?: string;
  cards?: { recruiterId: string; href: string; text: string; name?: string | null }[];
  debug?: { trace: number[]; linksNow: number; docHeight: number; viewport: number };
}

/**
 * The screen's usable area, sent with every sourcing job (0.8.1, Sofia, Oct 28 2026): the extension
 * places its LinkedIn window from the top of it to the bottom. An older extension ignores it.
 */
export function screenArea(): { left: number; top: number; width: number; height: number } | null {
  const s = globalThis.screen as (Screen & { availLeft?: number; availTop?: number }) | undefined;
  if (!s?.availWidth || !s?.availHeight) return null;
  return { left: s.availLeft ?? 0, top: s.availTop ?? 0, width: s.availWidth, height: s.availHeight };
}

/** Open (or reuse) the sourcing tab on one list page and read its cards. */
export async function sourcePageInExtension(url: string): Promise<ListResult> {
  const r = await message<ListResult>({ type: 'source-page', job: { url, screen: screenArea() } }, 90_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Stage 2 (Oct 20 2026): running the role's keyword search in the project needs 0.4.0. */
export const SEARCH_MIN_VERSION = '0.5.1';
export const canSearch = (version: string | null | undefined) =>
  Boolean(version) && (version as string).localeCompare(SEARCH_MIN_VERSION, undefined, { numeric: true }) >= 0;

export interface SearchResult {
  ok: boolean;
  step: string;
  error?: string;
  detail?: string;
  url?: string;
  filters?: string[];
}

/** Run the keyword search in the sourcing window: Recruiter opens it by its address (0.5.0, Oct 22 2026). */
export async function sourceSearchInExtension(keywords: string, projectId?: string | null): Promise<SearchResult> {
  const r = await message<SearchResult>({ type: 'source-search', job: { keywords, ...(projectId ? { projectId } : {}), screen: screenArea() } }, 90_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

export interface PipelineResult {
  ok: boolean;
  step: string;
  error?: string;
  results?: { recruiterId: string; status: 'saved' | 'already' | 'not_found' | 'failed' }[];
}

/** "Save to pipeline" for these people on the project search page the run just read (0.5.2, Oct 22 2026). */
export async function sourceSaveInExtension(ids: string[]): Promise<PipelineResult> {
  const r = await message<PipelineResult>({ type: 'source-save', job: { ids } }, 120_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** One line per search refusal, saying what to do. */
export function explainSearch(r: SearchResult): string {
  switch (r.error) {
    case 'search_not_started':
      return `LinkedIn did not run the search${r.detail ? ` (${r.detail})` : ''}. Check the LinkedIn window; you can also paste the search below into Recruiter yourself and paste its address here instead.`;
    case 'unknown_type':
    case 'not_a_search_page_url':
    case 'no_keywords':
      return 'Your FINN Outreach Sender is too old to run the search. Download it again (the box at the top of this step) and click ↻ in chrome://extensions.';
    default:
      return explainList(r as ListResult);
  }
}

/** A fit's full profile, read in the sourcing run's own small window (visible, so it renders). */
export async function sourceCaptureInExtension(recruiterId: string): Promise<CaptureResult> {
  const url = `https://www.linkedin.com/talent/profile/${encodeURIComponent(recruiterId)}`;
  const r = await message<CaptureResult>({ type: 'source-capture', job: { url, screen: screenArea() } }, 120_000);
  return r || { ok: false, step: 'extension', error: 'no_answer' };
}

/** Close the sourcing tab and come back to this page. */
export async function sourceDone(): Promise<void> {
  await message({ type: 'source-done' }, 10_000);
}

export function explainList(r: ListResult): string {
  switch (r.error) {
    case 'no_cards_read':
      return 'No candidates showed on this LinkedIn page in time. Check the LinkedIn window, then Continue — or Stop if the list has ended.';
    case 'linkedin_warning':
      return `LinkedIn showed a warning (“${r.detail || 'unusual activity'}”). Sourcing stopped — check the tab before you go on.`;
    case 'not_logged_in':
      return 'You are not signed in to LinkedIn Recruiter in this Chrome.';
    case 'not_a_project_list':
    case 'not_a_project_list_url':
      return 'That page is not a Recruiter project list. Check the job’s Recruiter link.';
    case 'unknown_type':
      return 'Your FINN Outreach Sender is too old for sourcing. Download it again (Outreach queue › Send) and click ↻ in chrome://extensions.';
    default:
      return explain(r as SendResult);
  }
}

/** One line per capture refusal. */
export function explainCapture(r: CaptureResult): string {
  switch (r.error) {
    case 'no_profile_tab':
      return 'No LinkedIn Recruiter profile is open in this Chrome. Open the profile in Recruiter, then click again.';
    case 'not_a_profile':
      return 'The open LinkedIn tab is not a Recruiter profile.';
    case 'not_logged_in':
      return 'You are not signed in to LinkedIn Recruiter in this Chrome.';
    case 'empty':
      return 'The profile page showed no text yet. Try again in a moment.';
    case 'unknown_type':
      return 'Your FINN Outreach Sender is too old to read profiles. Download it again below and click ↻ in chrome://extensions.';
    default:
      return explain(r as SendResult);
  }
}

/** What the recruiter reads for each refusal — one line, in their terms. */
export function explain(r: SendResult): string {
  switch (r.error) {
    case 'not_logged_in':
      return 'You are not signed in to LinkedIn Recruiter in this Chrome. Sign in, then start again.';
    case 'links_not_accepted':
      return 'LinkedIn did not take the links in this InMail as links, so nothing was sent. Try “Test fill” and tell Sofia what you see.';
    // 0.7.2 (Sofia, Oct 27 2026): the composer shows fewer (or more) lines or paragraphs than the InMail.
    case 'paragraphs_lost':
      return `LinkedIn lost the paragraphs of this InMail${r.detail ? ` (${r.detail})` : ''}, so nothing was sent. It is still open in the tab — try “Test fill” and tell Sofia what you see.`;
    case 'followup_days_not_found':
    case 'followup_days_select_not_found':
    case 'followup_days_save_not_found':
    case 'followup_days_not_set':
      return `The follow-up days could not be set in LinkedIn${r.detail ? ` (${r.detail})` : ''}. Nothing was sent. Untick the follow-up or try “Test fill”.`;
    case 'composer_not_found':
      return `The InMail composer did not open on the page${r.detail ? ` (${r.detail})` : ''}. Open the candidate yourself and check.`;
    case 'already_contacted':
      return `Detected: already contacted — LinkedIn says: “${r.detail || 'this person was contacted recently'}” Nothing was typed or sent.`;
    case 'recipient_mismatch':
      return `The page does not show this candidate${r.detail ? ` (${r.detail})` : ''}. Nothing was typed.`;
    // Only extensions before 0.3.9 say this: they refused LinkedIn's own AI draft instead of replacing it.
    case 'composer_not_empty':
      return `LinkedIn had already put its own draft in the composer${r.detail ? ` (“${r.detail}”)` : ''}, and this version of the FINN Outreach Sender cannot replace it. Download the extension again (Send box), reload it, then start again. Nothing was sent.`;
    case 'body_overwritten':
      return `LinkedIn kept writing its own draft over ours${r.detail ? ` (“${r.detail}”)` : ''}. Nothing was sent; it is still open in the tab.`;
    case 'subject_not_accepted':
    case 'body_not_accepted':
      return 'LinkedIn did not take the text. Open the candidate yourself and paste it.';
    case 'send_button_not_found':
      return `The InMail was typed but no Send button was found${r.detail ? ` (${r.detail})` : ''}. It is still open in the tab.`;
    case 'send_button_disabled':
      return `The InMail was typed but Send stayed greyed out${r.detail ? ` (${r.detail})` : ''} — LinkedIn did not register the typing, or no InMail credits. It is still open in the tab.`;
    case 'wait_24h':
      return `LinkedIn still blocks a new message${r.detail ? ` (“${r.detail}”)` : ''}. Try again once the 24 hours are over.`;
    case 'followup_add_not_found':
    case 'followup_fields_not_found':
    case 'followup_not_empty': // extensions before 0.3.9
    case 'followup_not_accepted':
      return `The InMail was typed, but LinkedIn's follow-up could not be added (${r.error}${r.detail ? `; ${r.detail}` : ''}). Nothing was sent; it is still open in the tab.`;
    case 'left_linkedin':
      return 'LinkedIn sent the tab somewhere else. Sign in, then start again.';
    case 'busy':
      return 'The extension is still working on the previous InMail.';
    case 'sourcing_window_open':
      return 'A sourcing run is using its LinkedIn window. Let it finish or stop it (or close that LinkedIn window), then press Start again.';
    case 'send_running':
      return 'InMails are being sent from another People Space tab. Wait for that run to end, then try again.';
    case 'no_answer':
      return 'The extension did not answer. Is it enabled in chrome://extensions?';
    default:
      return `Stopped at “${r.step}”${r.error ? `: ${r.error}` : ''}${r.detail ? ` (${r.detail})` : ''}.`;
  }
}
